<script setup>
import {computed, h, onMounted, render, unref, useTemplateRef, watch} from 'vue';
import {MarkerClusterer} from '@googlemaps/markerclusterer';
import {
    activeStore,
    DEFAULT_MAP_CENTER,
    DEFAULT_MAP_ZOOM,
    filteredStores,
    radius,
    searchCoordinates,
    stores,
} from '../../stores/storeMap';
import googleMaps from './googleMaps';
import StoreInfoWindow from './StoreInfoWindow.vue';
import {GOOGLE_MAPS_STORE_MAP_ID} from '../../config';
import {tap} from '../../utils';

const [
    {InfoWindow, RenderingType, Circle},
    {AdvancedMarkerElement, PinElement, Marker},
    ,
    {LatLngBounds},
] = await googleMaps();

/** @type {import('vue').TemplateRef<google.maps.MapElement>} */
const map = useTemplateRef('map');
/** @type {import('vue').ComputedRef<google.maps.Map>} */
const innerMap = computed(() => map.value?.innerMap);
/**
 * Generate a marker for each store
 * @type {import('vue').ComputedRef<Map<Store, google.maps.marker.AdvancedMarkerElement>>}
 */
const storeMarkerMap = computed(() => {
    return new Map(stores.value.map(store => {
        const marker = new AdvancedMarkerElement({
            title: store.name,
            position: store.coordinates,
            content: new PinElement({
                background: '#ff5048',
                glyphSrc: '/images/logo/logo-icon-white.svg',
            }),
        });
        marker.addListener('gmp-click', () => activeStore.value = store);
        return [store, marker];
    }));
});
/**
 * Only visible markers
 * @type {import('vue').ComputedRef<google.maps.marker.AdvancedMarkerElement[]>}
 */
const storeMarkers = computed(() => filteredStores.value.map(store => storeMarkerMap.value.get(store)));
/** @type {MarkerClusterer} */
let markerClusterer = null;
/** @type {google.maps.InfoWindow} */
const infoWindow = new InfoWindow();
infoWindow.addListener('closeclick', () => activeStore.value = null);
/** @type {import('vue').ComputedRef<google.maps.LatLngBounds>} */
const mapBounds = computed(() => {
    if (!storeMarkers.value.length) {
        return new Circle({center: searchCoordinates.value, radius: radius.value}).getBounds();
    }
    return tap(new LatLngBounds(), bounds => {
        storeMarkers.value.forEach(marker => bounds.extend(marker.position));
        if (searchCoordinates.value) {
            bounds.extend(searchCoordinates.value);
        }
    });
});

/**
 * @param {Store|import('vue').Ref<Store>} store
 */
function showStoreInfo(store) {
    store = unref(store);
    if (!store) {
        infoWindow.close();
        return;
    }
    const marker = storeMarkerMap.value.get(store);
    const cluster = getMarkerCluster(marker);
    if (cluster) {
        innerMap.value.fitBounds(cluster.bounds);
    }
    renderInfoWindow(store);
    infoWindow.open(innerMap.value, marker);
    if (window.innerWidth < 1024) { // Scroll to map on mobile
        map.value.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
        });
    }
}

function renderInfoWindow(store) {
    const headerContent = document.createElement('strong');
    headerContent.style.fontSize = '18px';
    headerContent.style.fontWeight = '500';
    headerContent.textContent = store.name;
    infoWindow.setHeaderContent(headerContent);
    const content = document.createElement('div');
    render(h(StoreInfoWindow, {store}), content);
    infoWindow.setContent(content);
}

/**
 * @param {AdvancedMarkerElement} marker
 * @returns {import('@googlemaps/markerclusterer').Cluster|null}
 */
function getMarkerCluster(marker) {
    const cluster = markerClusterer.clusters.find(cluster => cluster.markers.includes(marker));
    return cluster.count > 1 ? cluster : null;
}

watch(activeStore, () => {
    if (!innerMap.value) {
        return;
    }
    showStoreInfo(activeStore);
});

watch(storeMarkers, () => {
    if (!markerClusterer) {
        return;
    }
    markerClusterer.addMarkers(storeMarkers.value.filter(marker => !markerClusterer.markers.includes(marker)));
    markerClusterer.removeMarkers(markerClusterer.markers.filter(marker => !storeMarkers.value.includes(marker)));
});

watch([innerMap, mapBounds], () => {
    if (!innerMap.value) {
        return;
    }
    innerMap.value.fitBounds(mapBounds.value, 100);
});

onMounted(() => {
    innerMap.value.setOptions({
        minZoom: 6,
        maxZoom: 16,
        fullscreenControl: false,
        mapTypeControl: false,
        streetViewControl: false,
        cameraControl: false,
        zoomControl: true,
    });
    innerMap.value.addListener('click', () => activeStore.value = null);
    markerClusterer = new MarkerClusterer({
        markers: storeMarkers.value,
        map: innerMap.value,
        renderer: {
            render({count, position}, stats, map) {
                return new AdvancedMarkerElement({
                    map,
                    position,
                    zIndex: Number(Marker.MAX_ZINDEX) + count,
                    title: `${count} magasins dans cette zone`,
                    content: new PinElement({
                        background: '#ff5048',
                        glyphText: String(count),
                        glyphColor: '#ffffff',
                    }),
                });
            },
        },
    });
});
</script>

<template>
    <gmp-map ref="map"
             :center="DEFAULT_MAP_CENTER"
             :map-id="GOOGLE_MAPS_STORE_MAP_ID"
             :renderingType="RenderingType.VECTOR"
             :zoom="DEFAULT_MAP_ZOOM"
             class="store-list-map"/>
</template>
